import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthService } from '../services/auth/authService.service';
import { LOGOUT_IN_PROGRESS_KEY } from '../services/auth/logout.service';
import {
  ACCESS_TOKEN_KEY,
  AUTH_EXCHANGE_PATH,
  AUTH_REFRESH_PATH
} from '../services/auth/auth.constants';

@Injectable()
export class Interceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authRequest = this.#attachAccessToken(request);

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401 || this.#shouldSkipTokenRefresh(authRequest)) {
          if (error.status === 401) {
            this.#logoutIfNeeded();
          }

          return throwError(() => error);
        }

        const authService = this.injector.get(AuthService);

        return authService.refreshTokens().pipe(
          switchMap((refreshed) => {
            if (!refreshed) {
              this.#logoutIfNeeded();
              return throwError(() => error);
            }

            return next.handle(this.#attachAccessToken(request));
          })
        );
      })
    );
  }

  #attachAccessToken(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const byPass = request.headers.get('byPass');

    if (byPass === 'true') {
      return request;
    }

    const token = localStorage.getItem(ACCESS_TOKEN_KEY);

    if (!token) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  #shouldSkipTokenRefresh(request: HttpRequest<unknown>): boolean {
    if (request.headers.get('byPass') === 'true') {
      return true;
    }

    const url = request.url;

    return url.includes(AUTH_EXCHANGE_PATH) || url.includes(AUTH_REFRESH_PATH);
  }

  #logoutIfNeeded(): void {
    const path = window.location.pathname;

    if (path.includes('/login') || sessionStorage.getItem(LOGOUT_IN_PROGRESS_KEY)) {
      return;
    }

    const authService = this.injector.get(AuthService);
    authService.logout('token_expired');
  }
}
